import { ComboboxInput } from '@headlessui/react';
import { BaseButton } from '@kk/ui/components/Button';
import Icon from '@kk/ui/components/Icon';
import { cn } from 'cn';
import {
  type FocusEventHandler,
  type KeyboardEventHandler,
  type Ref,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

interface SearchInputProps {
  open: boolean;
  value: string;
  ref?: Ref<HTMLInputElement>;
  onBlur: FocusEventHandler<HTMLInputElement>;
  onChange: (value: string) => void;
  onClose: () => void;
  onFocus: () => void;
  onKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onKeyUp: KeyboardEventHandler<HTMLInputElement>;
}

export function SearchInput({
  open,
  value,
  ref,
  onBlur,
  onChange,
  onClose,
  onFocus,
  onKeyDown,
  onKeyUp,
}: SearchInputProps): React.ReactNode {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <>
      <button
        className="absolute inset-y-2 left-4 flex items-center border-none p-0"
        tabIndex={-1}
        onClick={() => inputRef.current?.focus()}
      >
        <Icon
          type="magnify"
          className={open ? 'text-blue-60' : 'text-neutral-100'}
          size="small"
        />
      </button>
      <ComboboxInput
        value={value}
        displayValue={() => value}
        placeholder={t('search.placeholder')}
        name="search"
        type="search"
        autoComplete="off"
        className={cn(
          'kunde:pl-12! kunde:rounded-[3rem]! w-full lg:w-96 kunde:border-2! ',
          'focus-visible:ring-2! focus:ring-blue-10! focus:border-blue-60!',
          'hover:bg-blue-5! active:bg-blue-5! placeholder:text-neutral-100!',
        )}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        ref={(node) => {
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
          inputRef.current = node;
        }}
      />
      {open ? (
        <div className="absolute right-0 inset-y-0">
          <div className="flex items-center h-full pr-3">
            {value !== '' ? (
              <>
                <BaseButton
                  role="button"
                  name="clear"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    onChange('');
                    inputRef.current?.focus();
                  }}
                  className="typ-button rounded kunde:font-normal text-neutral-100 py-1 px-2"
                >
                  {t('search.clear')}
                </BaseButton>
                <span className="border-neutral-40 mx-1 h-4 border-r" />
              </>
            ) : null}
            <BaseButton
              onClick={onClose}
              className="rounded ml-1 p-1 flex items-center justify-center"
            >
              <Icon
                type="cross"
                className="text-neutral-110 size-5"
                size="small"
              />
            </BaseButton>
          </div>
        </div>
      ) : null}
    </>
  );
}
